import {createContext, useState, useContext} from "react";
import {auth} from "../firebase-config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import DOMPurify from 'dompurify';
import FirebaseAuthListener from "../Components/Auth/FirebaseAuthListener";

const AuthContext = createContext();

// Validation utilities
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email || typeof email !== 'string') {
    throw new Error('Email is required');
  }
  if (!emailRegex.test(email)) {
    throw new Error('Invalid email format');
  }
  return email.toLowerCase().trim();
};

const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    throw new Error('Password is required');
  }
  if (password.length < 8) {
    throw new Error('Password must be at least 8 characters');
  }
  // Add more password requirements as needed
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\S]{8,}$/;
  if (!passwordRegex.test(password)) {
    throw new Error('Password must contain at least one uppercase letter, one lowercase letter, and one number');
  }
  return password;
};

const validateDisplayName = (displayName) => {
  if (!displayName || typeof displayName !== 'string') {
    throw new Error('Display name is required');
  }
  // Sanitize and validate display name
  const sanitized = DOMPurify.sanitize(displayName.trim());
  if (sanitized.length < 2 || sanitized.length > 50) {
    throw new Error('Display name must be between 2 and 50 characters');
  }
  // Remove any potentially harmful characters
  return sanitized.replace(/[<>{}]/g, '');
};

// API base URL for the dashboard (auth/admin endpoints).
// - Local dev:  set REACT_APP_API_URL=http://localhost:5001 in frontend/.env.local
// - Production: Firebase Hosting rewrites /api/** → Cloud Function, so use relative /api
export const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "/api");


export function AuthProvider({children}) {
  const [currentUser, setCurrentUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [error, setError] = useState(null);

  const login = async (email, password) => {
    try {
      setError(null);
      
      const validatedEmail = validateEmail(email);
      const validatedPassword = validatePassword(password);

      const userCredential = await signInWithEmailAndPassword(
        auth, 
        validatedEmail, 
        validatedPassword
      );

      // Get the user's UID and token
      const { uid } = userCredential.user;
      const token = await userCredential.user.getIdToken();

      // Fire-and-forget: record login for analytics. Never block or throw on failure.
      fetch(`${API_URL}/record-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uid })
      }).catch(() => {});

      return userCredential.user;
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid email or password");
      throw new Error("Invalid email or password");
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      setCurrentUser(null);
    } catch (error) {
      console.error("Logout error:", error);
      throw new Error("Error during logout");
    }
  };

  const signup = async (email, password, displayName) => {
    try {
      setError(null);

      const validatedEmail = validateEmail(email);
      const validatedPassword = validatePassword(password);
      const validatedDisplayName = validateDisplayName(displayName);

      // Create the user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        validatedEmail, 
        validatedPassword
      );
      
      const user = userCredential.user;

      // Update the user profile
      await updateProfile(user, {
        displayName: validatedDisplayName,
      });

      // Send request to create user document
      const response = await fetch(`${API_URL}/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: user.uid,  
          email: validatedEmail,
          displayName: validatedDisplayName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user document');
      }

      return user;
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError("An account with this email already exists");
      } else {
        setError(error.message || "Error creating account");
      }
      throw error;
    }
  };

  // Helper function to make authenticated API calls
  const makeAuthenticatedRequest = async (url, options = {}) => {
    console.log('🔐 Client auth check:', {
      hasCurrentUser: !!currentUser,
      userUid: currentUser?.uid,
      url: url
    });
    
    if (!currentUser) {
      console.error('❌ Client auth failed: No current user');
      throw new Error('User not authenticated');
    }

    const token = await currentUser.getIdToken();
    console.log('🔐 Client auth token obtained:', {
      tokenLength: token.length,
      tokenStart: token.substring(0, 20) + '...'
    });
    
    const fetchOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };
    
    console.log('🌐 Fetch options:', {
      method: fetchOptions.method,
      url: url,
      hasBody: !!fetchOptions.body,
      bodyLength: fetchOptions.body?.length
    });
    
    return fetch(url, fetchOptions);
  };

  const value = {
    currentUser,
    login,
    logout,
    signup,
    error,
    makeAuthenticatedRequest,
    setCurrentUser,
    authInitialized,
    setAuthInitialized
  };

  return (
    <AuthContext.Provider value={value}>
      <FirebaseAuthListener>
        {children}
      </FirebaseAuthListener>
    </AuthContext.Provider>
  );
}
export function useAuth() {
  return useContext(AuthContext);
}

